import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { registerAuthLost } from './lib/axios';
import { useAuthStore, useIsSuperAdmin } from './stores/authStore';
import Layout from './components/Layout';
import Login from './pages/Login';
import SelectTenant from './pages/SelectTenant';
import Dashboard from './pages/Dashboard';
import Entries from './pages/Entries';
import Contacts from './pages/Contacts';
import Campaigns from './pages/Campaigns';
import Cotisants from './pages/Cotisants';
import Imports from './pages/Imports';
import Checks from './pages/Checks';
import Cash from './pages/Cash';
import Reconciliation from './pages/Reconciliation';
import Assets from './pages/Assets';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Tenants from './pages/Tenants';
import Profile from './pages/Profile';
import AiCosts from './pages/AiCosts';
import BankProfiles from './pages/BankProfiles';

function Protected({ children }: { children: JSX.Element }) {
  const { user, tenant, needsTenantSelection } = useAuthStore();
  const isSuperAdmin = useIsSuperAdmin();
  if (!user) return <Navigate to="/login" replace />;
  if (!tenant) {
    // Platform super-admins can operate the back-office without an active tenant.
    if (isSuperAdmin) return children;
    return <Navigate to="/select-tenant" replace />;
  }
  if (needsTenantSelection) return <Navigate to="/select-tenant" replace />;
  return children;
}

/** Landing route: tenant users see the dashboard, tenant-less super-admins the back-office. */
function HomeRoute() {
  const tenant = useAuthStore((s) => s.tenant);
  return tenant ? <Dashboard /> : <Navigate to="/tenants" replace />;
}

function SuperAdminOnly({ children }: { children: JSX.Element }) {
  const isSuperAdmin = useIsSuperAdmin();
  if (!isSuperAdmin) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  const { t } = useTranslation();
  const { loadSession, logout, initialized } = useAuthStore();

  useEffect(() => {
    registerAuthLost(() => logout());
    loadSession();
  }, [loadSession, logout]);

  if (!initialized) {
    return <div className="p-8 text-ink-muted">{t('common.loading')}</div>;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/select-tenant" element={<SelectTenant />} />
      <Route
        path="/"
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<HomeRoute />} />
        <Route path="chart-of-accounts" element={<Navigate to="/settings" replace />} />
        <Route path="entries" element={<Entries />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="cotisants" element={<Cotisants />} />
        <Route path="imports" element={<Imports />} />
        <Route path="checks" element={<Checks />} />
        <Route path="cash" element={<Cash />} />
        <Route path="reconciliation" element={<Reconciliation />} />
        <Route path="assets" element={<Assets />} />
        <Route path="reports" element={<Reports />} />
        <Route path="settings" element={<Settings />} />
        <Route path="profile" element={<Profile />} />
        <Route
          path="tenants"
          element={
            <SuperAdminOnly>
              <Tenants />
            </SuperAdminOnly>
          }
        />
        <Route
          path="ai-costs"
          element={
            <SuperAdminOnly>
              <AiCosts />
            </SuperAdminOnly>
          }
        />
        <Route
          path="bank-profiles"
          element={
            <SuperAdminOnly>
              <BankProfiles />
            </SuperAdminOnly>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
